import {
  Directive, Input, TemplateRef, ViewContainerRef,
  OnInit, OnDestroy, ElementRef, Renderer2
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { FeatureAccessService } from '../Services/feature-access.service';

// ---------------------------------------------------------------------------
// Helper: inject / remove a small red "upgrade" badge on the parent element
// ---------------------------------------------------------------------------
function injectUpgradeBadge(
  renderer: Renderer2,
  hostEl: HTMLElement,
  router: Router,
  existing: HTMLElement | null
): HTMLElement {
  // Don't double-inject
  if (existing && hostEl.parentNode && hostEl.parentNode.contains(existing)) {
    return existing;
  }

  const badge: HTMLElement = renderer.createElement('span');
  renderer.addClass(badge, 'feature-upgrade-badge');
  renderer.setAttribute(badge, 'title', 'Feature upgrade required. Contact your Administrator.');

  const icon: HTMLElement = renderer.createElement('span');
  renderer.addClass(icon, 'material-symbols-outlined');
  renderer.appendChild(icon, renderer.createText('info'));
  renderer.appendChild(badge, icon);

  // Insert badge right after the host element inside its parent
  const parent = hostEl.parentNode;
  const next = hostEl.nextSibling;
  if (parent) {
    if (next) {
      renderer.insertBefore(parent, badge, next);
    } else {
      renderer.appendChild(parent, badge);
    }
  }

  return badge;
}

function removeUpgradeBadge(badge: HTMLElement | null): void {
  if (badge && badge.parentNode) {
    // Unlisten before removing
    if (badge['__upgradeListener']) {
      badge['__upgradeListener']();
    }
    badge.parentNode.removeChild(badge);
  }
}

// ---------------------------------------------------------------------------
// Structural directive: *featureVisible
// ---------------------------------------------------------------------------
@Directive({ selector: '[featureVisible]' })
export class FeatureVisibleDirective implements OnInit, OnDestroy {
  @Input('featureVisible') featureCode: string;
  private sub: Subscription;

  constructor(
    private tpl: TemplateRef<any>,
    private vcr: ViewContainerRef,
    private svc: FeatureAccessService
  ) {}

  ngOnInit() {
    this.sub = this.svc.accessMap.subscribe(map => {
      this.vcr.clear();
      if (map === null) return; // Loading, render nothing

      if (this.svc.isVisible(this.featureCode)) {
        this.vcr.createEmbeddedView(this.tpl);
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
  }
}

// ---------------------------------------------------------------------------
// Attribute directive: [featureNavState]
// Auto-injects upgrade badge when access is DISABLED
// ---------------------------------------------------------------------------
@Directive({ selector: '[featureNavState]' })
export class FeatureNavStateDirective implements OnInit, OnDestroy {
  @Input('featureNavState') featureCode: string;
  /** Set to false to suppress the upgrade-info badge when access is DISABLED */
  @Input() showBadge: boolean = true;
  private sub: Subscription;
  private upgradeBadge: HTMLElement | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private svc: FeatureAccessService,
    private router: Router
  ) {}

  ngOnInit() {
    this.hideWhileLoading();
    this.sub = this.svc.accessMap.subscribe(map => {
      if (map === null) {
        this.hideWhileLoading();
        return;
      }
      this.applyState();
    });
  }

  private hideWhileLoading() {
    const el = this.el.nativeElement;
    this.renderer.setStyle(el, 'opacity', '0');
    this.renderer.setStyle(el, 'pointer-events', 'none');
  }

  private applyState() {
    const el = this.el.nativeElement;
    const access = this.svc.getAccess(this.featureCode);
    this.renderer.setStyle(el, 'opacity', '1');

    if (this.svc.isNavClickable(this.featureCode)) {
      // Accessible — clean up any disabled state
      this.renderer.removeClass(el, 'nav-disabled');
      this.renderer.removeAttribute(el, 'disabled');
      this.renderer.setStyle(el, 'pointer-events', 'auto');
      this.renderer.removeAttribute(el, 'title');
      removeUpgradeBadge(this.upgradeBadge);
      this.upgradeBadge = null;
    } else {
      // DISABLED or no access — apply disabled styling
      this.renderer.addClass(el, 'nav-disabled');
      this.renderer.setAttribute(el, 'disabled', 'true');
      this.renderer.setStyle(el, 'pointer-events', 'none');
      this.renderer.setStyle(el, 'opacity', '0.45');
      this.renderer.removeAttribute(el, 'title');
      // Only inject badge for DISABLED (visible but locked), not HIDDEN
      if (access === 'DISABLED' && this.showBadge) {
        this.upgradeBadge = injectUpgradeBadge(
          this.renderer, el, this.router, this.upgradeBadge
        );
      } else {
        removeUpgradeBadge(this.upgradeBadge);
        this.upgradeBadge = null;
      }
    }
  }

  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
    removeUpgradeBadge(this.upgradeBadge);
  }
}

// ---------------------------------------------------------------------------
// Structural directive: *featureWritable
// ---------------------------------------------------------------------------
@Directive({ selector: '[featureWritable]' })
export class FeatureWritableDirective implements OnInit, OnDestroy {
  @Input('featureWritable') featureCode: string;
  private sub: Subscription;

  constructor(
    private tpl: TemplateRef<any>,
    private vcr: ViewContainerRef,
    private svc: FeatureAccessService
  ) {}

  ngOnInit() {
    this.sub = this.svc.accessMap.subscribe(map => {
      this.vcr.clear();
      if (map === null) return; // Loading, render nothing

      if (this.svc.isWritable(this.featureCode)) {
        this.vcr.createEmbeddedView(this.tpl);
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
  }
}

// ---------------------------------------------------------------------------
// Attribute directive: [featureActionState]
// ---------------------------------------------------------------------------
@Directive({ selector: '[featureActionState]' })
export class FeatureActionStateDirective implements OnInit, OnDestroy {
  @Input('featureActionState') featureCode: string;
  private sub: Subscription;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private svc: FeatureAccessService
  ) {}

  ngOnInit() {
    this.hideWhileLoading();
    this.sub = this.svc.accessMap.subscribe(map => {
      if (map === null) {
        this.hideWhileLoading();
        return;
      }
      this.applyState();
    });
  }

  private hideWhileLoading() {
    this.renderer.setStyle(this.el.nativeElement, 'visibility', 'hidden');
  }

  private applyState() {
    const el = this.el.nativeElement;
    this.renderer.setStyle(el, 'visibility', 'visible');

    if (this.svc.isWritable(this.featureCode)) {
      this.renderer.removeAttribute(el, 'disabled');
      this.renderer.removeClass(el, 'action-readonly');
      this.renderer.removeAttribute(el, 'title');
    } else {
      this.renderer.setAttribute(el, 'disabled', 'true');
      this.renderer.addClass(el, 'action-readonly');
      this.renderer.setAttribute(el, 'title', 'You have read-only access');
    }
  }

  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
  }
}

// ---------------------------------------------------------------------------
// Structural directive: *nonProjectFeatureVisible
// ---------------------------------------------------------------------------
@Directive({ selector: '[nonProjectFeatureVisible]' })
export class NonProjectFeatureVisibleDirective implements OnInit, OnDestroy {
  @Input('nonProjectFeatureVisible') featureCode: string;
  private sub: Subscription;

  constructor(
    private tpl: TemplateRef<any>,
    private vcr: ViewContainerRef,
    private svc: FeatureAccessService
  ) {}

  ngOnInit() {
    this.sub = this.svc.nonProjectAccessMap.subscribe(map => {
      this.vcr.clear();
      if (map === null) return; // Loading, render nothing

      if (this.svc.isNonProjectVisible(this.featureCode)) {
        this.vcr.createEmbeddedView(this.tpl);
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
  }
}

// ---------------------------------------------------------------------------
// Attribute directive: [nonProjectFeatureNavState]
// Auto-injects upgrade badge when access is DISABLED
// ---------------------------------------------------------------------------
@Directive({ selector: '[nonProjectFeatureNavState]' })
export class NonProjectFeatureNavStateDirective implements OnInit, OnDestroy {
  @Input('nonProjectFeatureNavState') featureCode: string;
  private sub: Subscription;
  private upgradeBadge: HTMLElement | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private svc: FeatureAccessService,
    private router: Router
  ) {}

  ngOnInit() {
    this.hideWhileLoading();
    this.sub = this.svc.nonProjectAccessMap.subscribe(map => {
      if (map === null) {
        this.hideWhileLoading();
        return;
      }
      this.applyState();
    });
  }

  private hideWhileLoading() {
    const el = this.el.nativeElement;
    this.renderer.setStyle(el, 'opacity', '0');
    this.renderer.setStyle(el, 'pointer-events', 'none');
  }

  private applyState() {
    const el = this.el.nativeElement;
    const access = this.svc.getNonProjectAccess(this.featureCode);
    this.renderer.setStyle(el, 'opacity', '1');

    if (this.svc.isNonProjectNavClickable(this.featureCode)) {
      // Accessible — clean up any disabled state
      this.renderer.removeClass(el, 'nav-disabled');
      this.renderer.removeAttribute(el, 'disabled');
      this.renderer.setStyle(el, 'pointer-events', 'auto');
      this.renderer.removeAttribute(el, 'title');
      removeUpgradeBadge(this.upgradeBadge);
      this.upgradeBadge = null;
    } else {
      // DISABLED or no access — apply disabled styling
      this.renderer.addClass(el, 'nav-disabled');
      this.renderer.setAttribute(el, 'disabled', 'true');
      this.renderer.setStyle(el, 'pointer-events', 'none');
      this.renderer.setStyle(el, 'opacity', '0.45');
      this.renderer.removeAttribute(el, 'title');

      // Only inject badge for DISABLED (visible but locked), not HIDDEN
      if (access === 'DISABLED') {
        this.upgradeBadge = injectUpgradeBadge(
          this.renderer, el, this.router, this.upgradeBadge
        );
      }
    }
  }

  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
    removeUpgradeBadge(this.upgradeBadge);
  }
}
